import { type QueryKey, type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import type {
  CreditNote,
  CreditNoteAPI,
} from 'receivable-invoices-js/react/api/models/credit-note';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

interface Params {
  id: string;
}

interface QueryResponse {
  data: CreditNoteAPI;
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/credit_notes/shared`;

export function fetchCreditNoteKey(id: string): QueryKey {
  return ['credit-note', id] as QueryKey;
}

export async function fetchCreditNote(params: Params, customOptions?: CustomOptions) {
  const { onError } = customOptions || {};

  try {
    const response = await qontoClient<QueryResponse>(`${URL}/${params.id}`);

    const { attributes, id } = response.data;
    const normalizedResponse = {
      ...attributes,
      id,
    };

    const creditNote = camelizeKeys(normalizedResponse) as CreditNote;

    return creditNote;
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

export type FetchCreditNoteResponse = Awaited<ReturnType<typeof fetchCreditNote>>;

export function useFetchCreditNote(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchCreditNoteResponse>>
) {
  return useQuery({
    queryKey: fetchCreditNoteKey(params.id),
    queryFn: () => fetchCreditNote(params, { ...customOptions }),
    ...queryOptions,
  });
}
